import Image from 'next/image'
import type {
	Elements,
	ElementModels,
	IContentItem,
} from '@kontent-ai/delivery-sdk'
import type {ReactElement} from 'react'
import {clsx} from 'clsx'
import {
	Icon as CIcon,
	type IconSize,
} from '@/products/flexible-web-toolkit/components/icon'
import {defaultMargins} from '@/products/flexible-web-toolkit/styles'
import {loadImageFromKontentAI} from '@/image-loader'
import type {Block} from '@/services/kontent-ai/types'
import type {IconWithTextContentItem} from '@/products/flexible-web-toolkit/blocks/icon-with-text'
import {StatsIcons} from '@/products/flexible-web-toolkit/components/stats-icon'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {RichTextRenderer} from '../../components/rich-text-renderer'

export type LargeBenefitsBlockContentItem = IContentItem<{
	icons: Elements.LinkedItemsElement<IconWithTextContentItem>
	columns: Elements.MultipleChoiceElement
	borderOption: Elements.MultipleChoiceElement
}>

const LargeBenefitIcon = ({
	block: icon,
	page,
	borderOption,
	...context
}: Parameters<Block<IconWithTextContentItem>>[0] & {
	borderOption: ElementModels.MultipleChoiceOption[]
}): ReactElement => {
	const {colorId: iconBrandColor} = colorWithFallback({
		blockColor: icon.elements.brandColor,
		pageConfig: page.elements.config[0],
		fallbackColorId: 'primary',
	})

	return (
		<div data-kontent-item-id={icon.system.id} key={icon.system.id}>
			<CIcon
				boldTitle={icon.elements.boldTitle[0]?.codename === 'bold'}
				brand={iconBrandColor ?? undefined}
				noBorder={borderOption[0]?.codename !== 'yes'}
				size={(icon.elements.iconSize[0]?.codename || 'lg') as IconSize}
				title={
					<span data-kontent-element-codename="title">
						{icon.elements.title}
					</span>
				}
			>
				<div className="relative h-full w-full">
					{icon.elements.icon[0] ? (
						<Image
							alt={icon.elements.icon[0].description ?? ''}
							data-kontent-element-codename="icon"
							fill
							loader={loadImageFromKontentAI}
							src={icon.elements.icon[0].url}
						/>
					) : null}
				</div>
			</CIcon>
			<RichTextRenderer
				data-kontent-element-codename="description"
				element={icon.elements.description}
				page={page}
				{...context}
			/>
		</div>
	)
}

export const LargeBenefitsBlockBlock: Block<LargeBenefitsBlockContentItem> = ({
	block,
	page,
	...context
}) => {
	return (
		<div className={clsx('container-wide text-blue-900', defaultMargins)}>
			<StatsIcons
				className="px-2"
				col={Number(block.elements.columns[0]?.name ?? 2)}
			>
				{block.elements.icons.map((icon) => (
					<LargeBenefitIcon
						block={icon}
						borderOption={block.elements.borderOption}
						key={icon.system.id}
						page={page}
						{...context}
					/>
				))}
			</StatsIcons>
		</div>
	)
}
